import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card } from "@/components/ui/card";
import {
  Users, UserPlus, ListChecks, AlertTriangle, Clock,
  Wallet, FileWarning, Bell, Activity
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DriveStatusCard from "@/components/DriveStatusCard";

function Avatar({ name, src, size = 44 }) {
  const [broken, setBroken] = useState(false);
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name}
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ width: size, height: size }}
        className="rounded-full object-cover border border-border"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-gradient-to-br from-teal/30 to-foreground/20 text-foreground font-medium text-sm flex items-center justify-center border border-border"
    >
      {initials || "?"}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent = "" }) {
  return (
    <Card className="p-5 border-border shadow-none flex flex-col gap-3" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon size={18} strokeWidth={1.5} className={accent || "text-muted-foreground"} />
      </div>
      <div className="text-3xl md:text-4xl font-light tracking-tighter font-[Outfit]">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const canSeeFinance = ["admin", "manager", "accountant", "sales"].includes(user?.role);
  const canSeeDriveSync = ["super_admin", "admin", "manager", "account_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/dashboard")
      .then(({ data }) => setStats(data))
      .catch((err) => {
        if (err?.response?.status !== 401) {
          logger.error("Dashboard fetch failed:", err);
        }
      });
    // Team widget — best-effort; some roles get 403 on /employees.
    api.get("/employees")
      .then(({ data }) => setTeam((data || []).filter((u) => u.active !== false && u.role !== "client")))
      .catch(() => { /* silent — content_creator etc. have no view_employees */ });
  }, []);

  const fmtMoney = (n) => `$${(n || 0).toLocaleString()}`;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="flex items-start gap-4">
          <Avatar name={user?.name} src={user?.avatar_url} size={72} />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Overview</div>
            <h1 className="heading text-3xl md:text-4xl font-semibold tracking-tight">
              Hello {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Here is your agency at a glance.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads" className="text-xs px-3 py-2 border border-border rounded-md hover:bg-muted" data-testid="quick-add-lead">+ New lead</Link>
          <Link to="/clients" className="text-xs px-3 py-2 border border-border rounded-md hover:bg-muted" data-testid="quick-add-client">+ New client</Link>
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Clients" value={stats?.total_clients ?? "—"} sub={`${stats?.active_clients ?? 0} active`} icon={Users} />
        <Kpi label="New Leads" value={stats?.new_leads ?? "—"} icon={UserPlus} accent="text-[#2563EB]" />
        <Kpi label="Open Tasks" value={stats?.open_tasks ?? "—"} sub={`${stats?.late_tasks ?? 0} late`} icon={ListChecks} accent="text-[#2563EB]" />
        <Kpi label="Pending Approvals" value={stats?.pending_approvals ?? "—"} icon={AlertTriangle} accent="text-[#F59E0B]" />
        {canSeeFinance && (
          <>
            <Kpi label="Monthly Revenue" value={fmtMoney(stats?.monthly_revenue)} icon={Wallet} accent="text-[#10B981]" />
            <Kpi label="Unpaid Invoices" value={stats?.unpaid_invoices_count ?? "—"} sub={fmtMoney(stats?.unpaid_amount)} icon={FileWarning} accent="text-[#EF4444]" />
          </>
        )}
        <Kpi label="My Open Tasks" value={stats?.my_open_tasks ?? "—"} icon={Clock} />
        <Kpi label="My Notifications" value={stats?.my_unread_notifs ?? "—"} icon={Bell} />
        {canSeeDriveSync && <DriveStatusCard />}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/leads" className="block">
          <Card className="p-6 border-border shadow-none hover:bg-muted/40 transition-colors h-full">
            <Activity size={20} strokeWidth={1.5} className="mb-3 text-[#2563EB]" />
            <div className="heading text-xl font-medium mb-1">Lead pipeline</div>
            <p className="text-sm text-muted-foreground">Track every prospect from first touch to closed-won.</p>
          </Card>
        </Link>
        <Link to="/tasks" className="block">
          <Card className="p-6 border-border shadow-none hover:bg-muted/40 transition-colors h-full">
            <ListChecks size={20} strokeWidth={1.5} className="mb-3 text-[#F59E0B]" />
            <div className="heading text-xl font-medium mb-1">Today's tasks</div>
            <p className="text-sm text-muted-foreground">See what's pending in your queue, sorted by deadline.</p>
          </Card>
        </Link>
        {canSeeFinance && (
          <Link to="/finance" className="block">
            <Card className="p-6 border-border shadow-none hover:bg-muted/40 transition-colors h-full">
              <Wallet size={20} strokeWidth={1.5} className="mb-3 text-[#10B981]" />
              <div className="heading text-xl font-medium mb-1">Cashflow</div>
              <p className="text-sm text-muted-foreground">Invoices, partials, overdue — keep money in motion.</p>
            </Card>
          </Link>
        )}
      </section>

      {/* Team — photos + role grid; only shows when /employees was reachable */}
      {team.length > 0 && (
        <section data-testid="dashboard-team">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">The crew</div>
              <h2 className="heading text-xl font-medium">Team · {team.length} active</h2>
            </div>
            <Link to="/employees" className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted">
              Manage →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {team.slice(0, 24).map((u) => (
              <Card key={u.id}
                    className="p-5 border-border shadow-none flex flex-col items-center text-center gap-3 hover:bg-muted/30 transition-colors"
                    data-testid={`team-member-${u.id}`}>
                <Avatar name={u.name} src={u.avatar_url} size={120} />
                <div className="min-w-0 w-full">
                  <div className="text-base font-medium truncate" title={u.name}>{u.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate"
                       title={(u.role || "").replace(/_/g, " ")}>
                    {(u.role || "").replace(/_/g, " ")}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
